import type {
  Lesson,
  LessonMaterial,
  ModuleDraft,
  MultipleChoiceQuestion
} from '../../../../../components/admin3/newProgram/masterclass/types';

/**
 * Generation modules (the wizard's `generatedContent.modules`, or a version's
 * `snapshot.modules`) → the Designer's ModuleDraft shape, so VersionPreview
 * and its "compared with" diff can be reused as they are.
 *
 * Identity is positional (`m0-l2`): the status endpoint serves no module /
 * lesson ids, so both sides of the comparison are keyed the same way and a
 * moved lesson reads as reworded rather than removed + added. The quiz's
 * correct answer is left unset on purpose: the wizard state holds the
 * encrypted value the status endpoint serves while a snapshot stores the
 * plaintext, so comparing them would flag every quiz as different.
 *
 * Read-only mapping: nothing here is ever written back into the wizard.
 */
export function generationModulesToDrafts(modules: any[] | null | undefined): ModuleDraft[] {
  return (Array.isArray(modules) ? modules : []).map(toModuleDraft);
}

function toModuleDraft(m: any, mi: number): ModuleDraft {
  const moduleId = `m${mi}`;
  const lessons: any[] = Array.isArray(m?.lessons) ? m.lessons : [];
  return {
    id: moduleId,
    sourceId: moduleId,
    title: m?.title || `Module ${mi + 1}`,
    objective: m?.reasoning || '',
    status: m?.status,
    completedLessonsCount: m?.completedLessonsCount,
    totalLessonsCount: m?.totalLessonsCount,
    lessons: lessons.map((l, li) => toLessonDraft(l, `${moduleId}-l${li}`, li))
  };
}

function toLessonDraft(l: any, lessonId: string, li: number): Lesson {
  const quiz: any[] = Array.isArray(l?.mcqs) && l.mcqs.length > 0 ? l.mcqs : l?.mcq ? [l.mcq] : [];
  const materials: any[] = Array.isArray(l?.materials) ? l.materials : [];
  return {
    id: lessonId,
    sourceId: lessonId,
    title: l?.title || `Lesson ${li + 1}`,
    description: l?.content || '',
    materials: materials.map((mat, i) => toMaterial(mat, `${lessonId}-mat${i}`)),
    feedbackQuestions: [],
    multipleChoiceQuestions: quiz.filter(Boolean).map((q, qi) => toQuestion(q, `${lessonId}-q${qi}`)),
    elliott: l?.elliott ?? null,
    status: l?.status,
    usedRag: l?.usedRag,
    reasoning: l?.reasoning || '',
    learningOutcomesServed: Array.isArray(l?.learningOutcomesServed) ? l.learningOutcomesServed : [],
    ragDocumentTitles: Array.isArray(l?.ragDocumentTitles) ? l.ragDocumentTitles : []
  };
}

function toMaterial(mat: any, fallbackId: string): LessonMaterial {
  return {
    id: String(mat?._id || fallbackId),
    articleId: mat?._id ? String(mat._id) : undefined,
    title: mat?.title || '',
    reference: mat?.description || '',
    thumbnail: mat?.thumbnail || undefined,
    source: 'library'
  };
}

function toQuestion(q: any, questionId: string): MultipleChoiceQuestion {
  const options: any[] = Array.isArray(q?.options) ? q.options : [];
  return {
    id: questionId,
    prompt: q?.question || '',
    options: options.map((o, oi) => ({ id: `${questionId}-o${oi}`, label: String(o ?? '') })),
    optionReasonings: Array.isArray(q?.optionReasonings) ? q.optionReasonings.map(String) : []
  };
}
